import React, { useState } from 'react'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Link
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/hooks/redux'
import { login } from '@/store/slices/authSlice'
import { API_BASE_URL } from '../config/api'

import logo from '@/assets/famalink.webp'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp'>('password')
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState('')
  const [userId, setUserId] = useState('')
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [resetOtp, setResetOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetStep, setResetStep] = useState<'email' | 'otp' | 'password'>('email')
  const [resetSuccess, setResetSuccess] = useState('')
  const [resetError, setResetError] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (data.success && (data.data.user.role === 'admin' || data.data.user.role === 'superadmin')) {
        handleLoginSuccess(data.data)
      } else if (data.success) {
        setError('Access denied. Admin privileges required.')
      } else {
        setError(data.message || 'Login failed')
      }
    } catch (err: any) {
      setError('Login failed. Please try again.')
      console.error('Login error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleOTPRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (loading || !email) return
    
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_BASE_URL}/auth/initiate-otp-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: email }),
      })

      const data = await response.json()

      if (data.success) {
        setOtpSent(true)
        setUserId(data.data.user_id)
        setError('')
      } else {
        setError(data.message || data.error?.message || 'Failed to send OTP')
      }
    } catch (err: any) {
      setError('Failed to send OTP. Please try again.')
      console.error('OTP request error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (loading || !otp || otp.length !== 6) return
    
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, otp_code: otp }),
      })

      const data = await response.json()

      if (data.success && (data.data.user.role === 'admin' || data.data.user.role === 'superadmin')) {
        handleLoginSuccess(data.data)
      } else if (data.success) {
        setError('Access denied. Admin privileges required.')
      } else {
        setError(data.message || data.error?.message || 'Invalid OTP')
      }
    } catch (err: any) {
      setError('OTP verification failed. Please try again.')
      console.error('OTP verify error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleLoginSuccess = (data: any) => {
    // Store token and user data (handled by Redux slice)
    dispatch(login({
      user: data.user,
      token: data.token
    }))
    navigate('/dashboard')
  }

  const handleForgotPasswordRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetLoading(true)
    setResetError('')
    setResetSuccess('')

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotEmail }),
      })

      const data = await response.json()

      if (data.success) {
        setResetStep('otp')
        setResetSuccess('OTP has been sent to your email')
      } else {
        setResetError(data.message || 'Failed to send reset email')
      }
    } catch (err: any) {
      setResetError('Failed to send reset email. Please try again.')
      console.error('Forgot password error:', err)
    } finally {
      setResetLoading(false)
    }
  }


  const handleResetPassword = async () => {
    setResetLoading(true)
    setResetError('')
    setResetSuccess('')

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match')
      setResetLoading(false)
      return
    }

    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters')
      setResetLoading(false)
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: forgotEmail,
          otp_code: resetOtp,
          new_password: newPassword
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setResetSuccess('Password reset successfully! You can now log in with your new password.')
        setTimeout(() => {
          handleCloseForgotPassword()
        }, 2000)
      } else {
        // Show specific error message from backend
        const errorMessage = data.message || data.error?.message || data.error || 'Failed to reset password'
        setResetError(errorMessage)
        console.error('Reset password failed:', data)
      }
    } catch (err: any) {
      setResetError('Network error. Please try again.')
      console.error('Reset password error:', err)
    } finally {
      setResetLoading(false)
    }
  }

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false)
    setForgotEmail('')
    setResetOtp('')
    setNewPassword('')
    setConfirmPassword('')
    setResetStep('email')
    setResetSuccess('')
    setResetError('')
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img 
              src={logo} 
              alt="FamaLink Admin Dashboard Logo" 
              style={{ height: '48px', width: 'auto', marginBottom: '16px' }}
            />
            
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant={loginMethod === 'password' ? 'contained' : 'outlined'}
                onClick={() => { setLoginMethod('password'); setOtpSent(false); setError(''); }}
              >
                Password
              </Button>
              <Button
                fullWidth
                variant={loginMethod === 'otp' ? 'contained' : 'outlined'}
                onClick={() => { setLoginMethod('otp'); setOtpSent(false); setError(''); }}
              >
                OTP Login
              </Button>
            </Box>

            {loginMethod === 'password' ? (
            <Box sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email or Phone Number"
                name="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && email && password) {
                    handlePasswordLogin(e as any)
                  }
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && email && password) {
                    handlePasswordLogin(e as any)
                  }
                }}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !email || !password}
                onClick={handlePasswordLogin}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{ cursor: 'pointer' }}
                >
                  Forgot Password?
                </Link>
              </Box>
            </Box>
            ) : (
            <Box component="form" onSubmit={otpSent ? handleOTPVerify : handleOTPRequest} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email-otp"
                label="Email or Phone Number"
                name="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={otpSent}
              />
              {otpSent && (
                <>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="otp"
                    label="Enter OTP"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    OTP has been sent to your email and phone number
                  </Typography>
                </>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || (otpSent && otp.length !== 6)}
              >
                {loading ? (otpSent ? 'Verifying...' : 'Sending OTP...') : (otpSent ? 'Verify OTP' : 'Send OTP')}
              </Button>
              {otpSent && (
                <Button
                  fullWidth
                  onClick={() => { setOtpSent(false); setOtp(''); }}
                  sx={{ mb: 2 }}
                >
                  Use different email/phone
                </Button>
              )}
            </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Admin access required
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordOpen} onClose={handleCloseForgotPassword} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            Enter your email address and we'll send you an email with instructions to reset your password.
          </Typography>

          {resetSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {resetSuccess}
            </Alert>
          )}
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleForgotPasswordRequest}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={resetLoading || !forgotEmail}
              sx={{ mb: 2 }}
            >
              {resetLoading ? 'Sending...' : 'Submit'}
            </Button>
          </Box>

          {/* Password Reset Section - Shows after OTP is sent */}
          {resetStep !== 'email' && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Reset Your Password
              </Typography>
              
              <TextField
                fullWidth
                label="OTP Code"
                value={resetOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                  setResetOtp(value)
                }}
                inputProps={{ maxLength: 6 }}
                placeholder="Enter 6-digit OTP"
                required
                sx={{ mb: 2 }}
              />

              {/* Password Fields - Hidden until OTP is complete */}
              {resetOtp.length === 6 && (
                <Box component="form" onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }}>
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    autoComplete="new-password"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    autoComplete="new-password"
                    sx={{ mb: 2 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={resetLoading || !newPassword || !confirmPassword}
                  >
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            Back to{' '}
            <Link
              component="button"
              variant="body2"
              onClick={handleCloseForgotPassword}
              sx={{ cursor: 'pointer', fontWeight: 600 }}
            >
              Log In
            </Link>
          </Typography>
        </DialogContent>
      </Dialog>
    </Container>
  )
}

export default Login
